import React, { useEffect, useState } from 'react';
import {
    Stack,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    List,
    ListItemButton,
    TextField,
    Button as MuiButton,
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { PostUIStyles } from './PostUI.styles';

import AspectRatio from '@mui/joy/AspectRatio';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/joy/Box';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import ShareIcon from '../../Icons/svg/share.svg';
import CommentIcon from '../../Icons/svg/comment.svg';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { Button } from '@mui/joy';
import { MoreVert, FlagOutlined, InfoOutlined, Close, Flag } from '@mui/icons-material';
import PostShareModal from '../../Components/PostShareModal';
import { useNavigate } from 'react-router-dom';
import { request } from '../../Utils/request';
import CircleIcon from '@mui/icons-material/Circle';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { format } from 'date-fns/format';
import { deletePost } from '../../store/slices/PostSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import CustomAlertDialog from '../../Components/CustomAlertDialog';
import { getAllPublicUrls } from '../../services/CommonFunctions';
import COLORS from '../../Utils/colors';
import { handleFollow, handleLike, handleBookmark } from './PostCommonApi';
import BottomModal from '../../Components/BottomModel';
import Comments from './Comments';
import ImageWithLoader from '../../Components/ImageWithLoader';

const REPORT_REASONS = [
    'I just don\'t like it',
    'Bullying or unwanted contact',
    'Suicide, self-injury or eating disorders',
    'Violence, hate or exploitation',
    'Selling or promoting restricted items',
    'Nudity or sexual activity',
    'Scam, fraud or spam',
    'False information',
    'Intellectual property',
    'Others',
] as const;

interface CardUIProps {
    item: any;
    isPreview?: boolean;
}



export const PostUI: React.FC<CardUIProps> = ({
    item,
    isPreview,
}) => {
    // Destructure all needed fields from item
    const {
        author = {},
        title = '',
        description = '',
        files = [],
        location = {},
        createdAt = '',
        place = {},
        isLiked = false,
        categories = [],
        linkTo = null,
        flagged = {}

    } = item || {};
    const displayName = author.username || '';
    const filekey = files.map((file: any) => file.fileKey);
    
    // Separate videos and images
    const videos = files.filter((file: any) => file.fileType?.includes('video') || file.mimeType?.includes('video'));
    const images = files.filter((file: any) => !file.fileType?.includes('video') && !file.mimeType?.includes('video'));
    
    const [openShareModal, setOpenShareModal] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [, setIsVideoPlaying] = useState(false);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [publicUrl, setPublicUrl] = useState<string[] | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [openAlertDialog, setOpenAlertDialog] = useState(false);
    const [commentModalOpen, setCommentModalOpen] = useState(false);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [selectedReportReason, setSelectedReportReason] = useState<string | null>(null);
    const [reportDescription, setReportDescription] = useState('');
    const [reportConfirmOpen, setReportConfirmOpen] = useState(false);
    const [reportSubmitting, setReportSubmitting] = useState(false);
    const [flaggedDetailsModalOpen, setFlaggedDetailsModalOpen] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    // Local state for optimistic updates
    const [localIsLiked, setLocalIsLiked] = useState(isLiked);
    const [localLikeCount, setLocalLikeCount] = useState(item.likeCount || 0);
    const [localCommentCount, setLocalCommentCount] = useState(item.commentCount || 0);
    const [localIsSaved, setLocalIsSaved] = useState(item.isSaved || false);

    // Sync local state when item changes (e.g., different post in list)
    useEffect(() => {
        setLocalIsLiked(item.isLiked ?? false);
        setLocalLikeCount(item.likeCount || 0);
        setLocalCommentCount(item.commentCount || 0);
        setLocalIsSaved(item.isSaved || false);
    }, [item._id, item.isLiked, item.likeCount, item.commentCount, item.isSaved]);
    
    // State for max image height
    const [maxImageHeight, setMaxImageHeight] = useState<number | null>(null);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const user = useSelector((state: RootState) => state.user.user);

    // Internal handlers for like and bookmark
    const handleLikeInternal = async () => {
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }
        
        // Optimistic update
        const newIsLiked = !localIsLiked;
        const newLikeCount = newIsLiked ? localLikeCount + 1 : localLikeCount - 1;
        
        setLocalIsLiked(newIsLiked);
        setLocalLikeCount(newLikeCount);
        
        // Call Redux action
        handleLike(item._id, dispatch, 'image');
    };

    const handleBookmarkInternal = async () => {
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }
        
        // Optimistic update
        setLocalIsSaved(!localIsSaved);
        
        // Call Redux action
        handleBookmark(item._id, dispatch, 'image');
    };

    const handleCommentInternal = () => {
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }
        setCommentModalOpen(true);
    };

    const handleShareInternal = () => {
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }
        setOpenShareModal(true);
    };
    const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
    const fetchPublicUrls = async () => {
        if (Array.isArray(filekey) && filekey.length > 0 && !isPreview) {
            try {
                const urls = await getAllPublicUrls(filekey);
                setPublicUrl(urls);
            } catch (error) {
                console.log('error fetching public urls', error);
            }
        }
    };


    // Minimum swipe distance (in px)
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
        if ('touches' in e) {
            setTouchStart(e.touches[0].clientX);
        } else {
            setTouchStart(e.clientX);
        }
    };

    const onTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
        if ('touches' in e) {
            setTouchEnd(e.touches[0].clientX);
        } else {
            setTouchEnd(e.clientX);
        }
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe && currentImageIndex < images.length - 1) {
            setCurrentImageIndex(prev => prev + 1);
        }
        if (isRightSwipe && currentImageIndex > 0) {
            setCurrentImageIndex(prev => prev - 1);
        }

        setTouchEnd(null);
        setTouchStart(null);
    };

    // Video handling functions
    const onVideoTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
        if ('touches' in e) {
            setTouchStart(e.touches[0].clientX);
        } else {
            setTouchStart(e.clientX);
        }
    };

    const onVideoTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
        if ('touches' in e) {
            setTouchEnd(e.touches[0].clientX);
        } else {
            setTouchEnd(e.clientX);
        }
    };

    const onVideoTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe && currentVideoIndex < videos.length - 1) {
            setCurrentVideoIndex(prev => prev + 1);
        }
        if (isRightSwipe && currentVideoIndex > 0) {
            setCurrentVideoIndex(prev => prev - 1);
        }

        setTouchEnd(null);
        setTouchStart(null);
    };

    const handleVideoPlay = () => {
        setIsVideoPlaying(true);
    };

    const handleVideoPause = () => {
        setIsVideoPlaying(false);
    };

    const TimeAgo = () => {
        if (!createdAt) return null;
        const date = new Date(createdAt);
        const timeAgo = formatDistanceToNow(date, { addSuffix: true });
        return <span>{timeAgo}</span>;
    };

    // Update imageUrl when currentImageIndex changes
    useEffect(() => {
        if (images.length > 0) {
            setImageUrl(images[currentImageIndex]?.fileURL || '');
        }
    }, [currentImageIndex, images]);

    // Calculate max height from all images
    useEffect(() => {
        if (images.length === 0) {
            setMaxImageHeight(null);
            return;
        }

        // Calculate container width (card maxWidth: min(400px, 90vw))
        const containerWidth = Math.min(400, window.innerWidth * 0.9);
        
        const calculateMaxHeight = async () => {
            // Preload all images to get their dimensions
            const imagePromises = images.map((img: any) => {
                return new Promise<number>((resolve) => {
                    const imageUrl = img.fileURL;
                    const imgElement = new Image();
                    
                    imgElement.onload = () => {
                        const naturalWidth = imgElement.naturalWidth;
                        const naturalHeight = imgElement.naturalHeight;
                        
                        if (naturalWidth > 0 && naturalHeight > 0) {
                            // Calculate height when image is scaled to container width
                            const scaledHeight = (naturalHeight / naturalWidth) * containerWidth;
                            resolve(scaledHeight);
                        } else {
                            resolve(0);
                        }
                    };
                    
                    imgElement.onerror = () => {
                        resolve(0);
                    };
                    
                    imgElement.src = imageUrl;
                });
            });
            
            const calculatedHeights = await Promise.all(imagePromises);
            const maxHeight = Math.max(...calculatedHeights.filter(h => h > 0));
            
            if (maxHeight > 0) {
                setMaxImageHeight(maxHeight);
            }
        };
        
        calculateMaxHeight();
    }, [images]);



    const deletePostData = async (id: string) => {
        try {
            await request(`/posts/${id}`, {
                method: 'DELETE',
                authorization: true,
            });
            dispatch(deletePost(id));

        } catch (error) {
            console.log('error', error);
        }
    };

    const openReportFlow = () => {
        setAnchorEl(null);
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }
        setSelectedReportReason(null);
        setReportDescription('');
        setReportModalOpen(true);
    };

    const openReportConfirm = () => {
        if (!selectedReportReason) return;
        setReportModalOpen(false);
        setReportConfirmOpen(true);
    };

    const submitReport = async () => {
        const postId = item?._id;
        const reason = selectedReportReason;
        if (!postId || !reason) return;
        setReportSubmitting(true);
        try {
            await request(`/posts/${postId}/report`, {
                method: 'POST',
                authorization: true,
                body: {
                    reason,
                    description: reason === 'Others' ? reportDescription : (reportDescription || undefined),
                },
            });
            setReportConfirmOpen(false);
            setSelectedReportReason(null);
            setReportDescription('');
        } catch (error) {
            console.log('error reporting post', error);
        } finally {
            setReportSubmitting(false);
        }
    };

    const handleReportListingClick = () => {
        if (reportSubmitting) return;
        submitReport();
    };


    return (
        <>
            <CustomAlertDialog
                open={openAlertDialog}
                title="Confirm Action"
                content="Are you sure you want to proceed?"
                onClose={() => setOpenAlertDialog(false)}
                onAgree={() => {
                    // handle agree action
                    setOpenAlertDialog(false);
                    deletePostData(item?._id);
                }}
                agreeText="Yes"
                disagreeText="No"
            />
            <BottomModal
                open={commentModalOpen}
                onClose={() => setCommentModalOpen(false)}
                title="Comments"
                children={<Comments postId={item._id} onCommentAdded={() => setLocalCommentCount((c: number) => c + 1)} />}
            />
            <BottomModal
                open={reportModalOpen}
                onClose={() => { setReportModalOpen(false); setSelectedReportReason(null); setReportDescription(''); }}
                title="Report"
                maxHeight="70vh"
            >
                <Box sx={{ px: 0, pb: 2 }}>
                    <Typography level="body-md" color="neutral" sx={{ mb: 2, px: 2 }}>
                        Why are you reporting this post?
                    </Typography>
                    <List disablePadding>
                        {REPORT_REASONS.map((reason) => (
                            <ListItemButton
                                key={reason}
                                selected={selectedReportReason === reason}
                                onClick={() => setSelectedReportReason(reason)}
                                sx={{ py: 1.5 }}
                            >
                                <Typography level="body-sm">{reason}</Typography>
                            </ListItemButton>
                        ))}
                    </List>
                    {selectedReportReason === 'Others' && (
                        <Box sx={{ px: 2, pt: 1 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Additional details (optional)"
                                value={reportDescription}
                                onChange={(e) => setReportDescription(e.target.value)}
                                variant="outlined"
                                size="small"
                            />
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, px: 2 }}>
                        <Button
                            variant="solid"
                            color="primary"
                            disabled={!selectedReportReason}
                            onClick={openReportConfirm}
                        >
                            Continue
                        </Button>
                    </Box>
                </Box>
            </BottomModal>
            <BottomModal
                open={flaggedDetailsModalOpen}
                onClose={() => setFlaggedDetailsModalOpen(false)}
            >
                <Box sx={{ px: 2, pb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 ,marginTop:4}}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CircleIcon sx={{ fontSize: 6, color: '#B3261E' }} />
                            <Typography level="body-sm" fontWeight={600} >
                                Post Reported
                            </Typography>
                            {flagged?.flaggedAt && (
                                <Typography level="body-sm" color="neutral" sx={{ ml: 'auto',fontSize:12 }}>
                                    {format(new Date(flagged.flaggedAt), 'dd/MM/yyyy')} {format(new Date(flagged.flaggedAt), 'HH:mm')}
                                </Typography>
                                
                            )}
                        
                        </Box>
                        <Box>
                        <Typography level="body-sm" fontWeight={600} sx={{ mb: 0.5 }}>
                                    Report reason
                                </Typography>
                        </Box>
                        {flagged?.reason && (
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 6,
                                    border: '1px solid #FF383C',
                                    bgcolor: 'rgba(179, 38, 30, 0.08)',
                                }}
                            >
                                
                                <Typography level="body-sm" color="neutral" sx={{color:'#FF383C'}}>
                                    {flagged.reason}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </BottomModal>
            <Dialog
                open={reportConfirmOpen}
                onClose={() => !reportSubmitting && setReportConfirmOpen(false)}
                aria-labelledby="report-confirm-title"
            >
                <DialogTitle id="report-confirm-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Confirm Report Submission</span>
                    <IconButton size="sm" onClick={() => !reportSubmitting && setReportConfirmOpen(false)} aria-label="close">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <InfoOutlined color="action" sx={{ mt: 0.25 }} />
                        <DialogContentText>
                            You are about to report this post for review. If it violates our content policies, appropriate action will be taken. Your report remains confidential.
                        </DialogContentText>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <MuiButton
                        type="button"
                        variant="contained"
                        color="error"
                        disabled={reportSubmitting}
                        onClick={handleReportListingClick}
                    >
                        {reportSubmitting ? 'Submitting...' : 'Report listing'}
                    </MuiButton>
                </DialogActions>
            </Dialog>
            <Box>

            <Card
                variant="outlined"
                sx={[PostUIStyles.card, { '--Card-radius': (theme) => theme.vars.radius.xs }]}
                >
                <CardContent orientation="horizontal" sx={PostUIStyles.cardContent}>
                    <Box sx={PostUIStyles.avatarContainer}>
                        <Avatar
                            sx={PostUIStyles.avatar}
                            src={author?.profilePhoto?.url}
                            onClick={() => {
                                if (!isLoggedIn) {
                                    navigate('/login');
                                    return;
                                }
                                navigate(`/profile/${author._id}`);
                            }}
                            >
                            {displayName?.charAt(0)}
                        </Avatar>
                    </Box>
                    <Typography
                    onClick={() => {
                        if (!isLoggedIn) {
                            navigate('/login');
                            return;
                        }
                        navigate(`/profile/${author._id}`);
                    }}
                        sx={PostUIStyles.displayName}
                        noWrap
                        title={displayName}
                        >
                        {displayName}
                    </Typography>
                    <Box sx={PostUIStyles.actionButtonsContainer}>
                        {flagged?.isFlagged && (
                            <Box
                                component="span"
                                onClick={() => setFlaggedDetailsModalOpen(true)}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1.5,
                                    py: 0.5,
                                    // bgcolor: '#B3261E',
                                    color: '#FF383C',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    border: '1px solid #FF383C',
                                    bgcolor: 'rgba(179, 38, 30, 0.08)',
                                    borderRadius:"4px"
                                }}
                            >
                                <Flag sx={{ fontSize: 14 ,color:'#FF383C'}} />
                                Reported
                            </Box>
                        )}
                        {!isPreview && item?.author?.username !== user?.username && (
                            <Button variant="outlined"
                            sx={PostUIStyles.followButton(item?.isFollowing)}
                            onClick={async () => {
                                if (!isLoggedIn) {
                                    navigate('/login');
                                    return;
                                }
                                if(item?.isFollowing ){
                                    return;
                                }
                                const userId = item?.author?._id || item?.author?.id;
                                const postId = item?._id;
                                
                                handleFollow(userId, postId, dispatch, 'image');
                            }}>{item?.isFollowing ? "Following" : item.isRequested ?"Requested":"Follow"}</Button>
                        )}
                        <PostShareModal 
                            open={openShareModal} 
                            onClose={() => setOpenShareModal(false)}
                            postId={item?._id}
                            title={item?.title || item?.description || 'Post'}
                        />
                        {!isPreview && (
                            <div>
                                <IconButton
                                    aria-label="more"
                                    id="long-button"
                                    aria-controls={open ? 'long-menu' : undefined}
                                    aria-expanded={open ? 'true' : undefined}
                                    aria-haspopup="true"
                                    onClick={handleClick}
                                >
                                    <MoreVert />
                                </IconButton>
                                <Menu
                                    id="long-menu"
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={() => setAnchorEl(null)}
                                    slotProps={{
                                        paper: {
                                            style: PostUIStyles.menuPaper,
                                        },
                                        list: {
                                            'aria-labelledby': 'long-button',
                                        },
                                    }}
                                >
                                    {item?.author?.username === user?.username && (
                                        <>
                                            <MenuItem onClick={() => { setAnchorEl(null); navigate('/post/input', { state: item }); }}>
                                                <Typography>Edit</Typography>
                                            </MenuItem>
                                            <MenuItem onClick={() => { setAnchorEl(null); setOpenAlertDialog(true); }}>
                                                <Typography>Delete</Typography>
                                            </MenuItem>
                                        </>
                                    )}
                                    {item?.author?.username !== user?.username && (
                                        <MenuItem onClick={openReportFlow}>
                                        <FlagOutlined sx={{ color: '#d32f2f', mr: 1, fontSize: 20 }} />
                                        <Typography sx={{ color: '#d32f2f' }}>Report listing</Typography>
                                    </MenuItem>
                               )}
                                </Menu>
                            </div>
                        )}
                    </Box>
                </CardContent>

                {/* Show either images OR videos, with videos taking priority */}
                {videos.length > 0 ? (
                    // Video Preview
                    <CardOverflow>
                        <AspectRatio key={`video-${currentVideoIndex}`}>
                            <Box
                                sx={PostUIStyles.imageContainer}
                                onTouchStart={videos.length > 1 ? onVideoTouchStart : undefined}
                                onTouchMove={videos.length > 1 ? onVideoTouchMove : undefined}
                                onTouchEnd={videos.length > 1 ? onVideoTouchEnd : undefined}
                                onMouseDown={videos.length > 1 ? onVideoTouchStart : undefined}
                                onMouseMove={videos.length > 1 ? onVideoTouchMove : undefined}
                                onMouseUp={videos.length > 1 ? onVideoTouchEnd : undefined}
                            >
                                <video
                                    src={videos[currentVideoIndex]?.fileURL}
                                    controls
                                    style={PostUIStyles.video}
                                    onPlay={handleVideoPlay}
                                    onPause={handleVideoPause}
                                    onLoadStart={() => {
                                        console.log('video loading');
                                    }}
                                    onError={() => {
                                        console.log('video error');
                                    }}
                                />
                                {videos.length > 1 && (
                                    <Box sx={PostUIStyles.imageIndicators}>
                                        {videos.map((_: any, index: number) => (
                                            <CircleIcon
                                                key={index}
                                                sx={PostUIStyles.imageIndicator(index === currentVideoIndex)}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </AspectRatio>
                    </CardOverflow>
                ) : images.length > 0 ? (
                    // Image Preview
                    <CardOverflow>
                        <Box
                            sx={{
                                width: '100%',
                                height: maxImageHeight || 'auto',
                                minHeight: maxImageHeight ? 0 : 100,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                sx={PostUIStyles.imageContainer}
                                onTouchStart={imageUrl ? onTouchStart : undefined}
                                onTouchMove={imageUrl ? onTouchMove : undefined}
                                onTouchEnd={imageUrl ? onTouchEnd : undefined}
                                onMouseDown={imageUrl ? onTouchStart : undefined}
                                onMouseMove={imageUrl ? onTouchMove : undefined}
                                onMouseUp={imageUrl ? onTouchEnd : undefined}
                            >
                                <ImageWithLoader
                                    src={publicUrl?.[currentImageIndex] || images[currentImageIndex]?.fileURL || imageUrl}
                                    alt={title}
                                    loading="lazy"
                                    style={PostUIStyles.image}
                                    draggable={false}
                                    containerSx={{
                                        width: '100%',
                                        height: '100%',
                                    }}
                                    onLoad={() => {
                                    }}
                                    onError={() => {
                                        fetchPublicUrls();
                                    }}
                                />
                                {images.length > 1 && (
                                    <Box sx={PostUIStyles.imageIndicators}>
                                        {images.map((_: any, index: number) => (
                                            <CircleIcon
                                                key={index}
                                                sx={PostUIStyles.imageIndicator(index === currentImageIndex)}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </CardOverflow>
                ) : null}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <Typography sx={PostUIStyles.title}>{title}</Typography>
                    <CardContent>
                        <Typography sx={PostUIStyles.description(isExpanded)}>
                            {description}
                        </Typography>
                        {description.length > 30 && (
                            <Typography
                            onClick={() => setIsExpanded(!isExpanded)}
                            sx={PostUIStyles.showMoreButton}
                            >
                                {isExpanded ? 'Show less' : 'Show more'}

                            </Typography>
                        )}
                        <Box>
                        </Box>
                    </CardContent>
                </Box>
                {categories.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                        {categories.map((item: any, index: number) => (
                            <React.Fragment key={index}>
                                <Typography sx={{ color: '#49454F', fontWeight: '400' }}>
                                    {item}
                                </Typography>
                                {index < categories.length - 1 && (
                                    <CircleIcon sx={{ fontSize: 8, color: '#E0E0E0' }} />
                                )}
                            </React.Fragment>
                        ))}
                    </Box>
                )}
                {/* Location */}
                {location?.name && (
                    <Stack 
                        direction="row" 
                        onClick={() => {
                            if (!location?.name) return;
                            
                            // If coordinates are available, use them for precise location
                            if (location?.coordinates && Array.isArray(location.coordinates) && location.coordinates.length === 2) {
                                const [lng, lat] = location.coordinates;
                                // Open Google Maps with coordinates
                                window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
                                
                            } else {
                                // Otherwise, search by location name
                                const encodedName = encodeURIComponent(location.name);
                                window.open(`https://www.google.com/maps/search/?api=1&query=${encodedName}`, '_blank');
                            }
                        }}
                        sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.7
                            }
                        }}
                    >
                        <LocationOnIcon sx={PostUIStyles.locationIcon} />
                        <Typography sx={PostUIStyles.locationText}>{location?.name}</Typography>
                    </Stack>
                )}
                {/* Places */}
                {place?._id && place?.name && (
                    <Stack direction="row" onClick={() => {
                        if (!isLoggedIn) {
                            navigate('/login');
                            return;
                        }
                        navigate(`/places/${place?._id}`);
                    }} >
                        <MapOutlinedIcon sx={PostUIStyles.placeIcon} />
                        <Typography sx={PostUIStyles.placeText}>{place?.name}</Typography>
                    </Stack>
                )}
                <Typography sx={PostUIStyles.timeAgo}>{TimeAgo()}</Typography>

            </Card>
            {linkTo &&
                <Box
                onClick={() => {
                    if (!isLoggedIn) {
                        navigate('/login');
                        return;
                    }
                    if(linkTo?.module === "Trip"){
                        navigate(`/trips/${linkTo.id}?type=trip`);
                    }else{
                        navigate(`/trips/${linkTo.id}?type=itinerary`);
                    }
                }}
                // orientation="horizontal"
                sx={[PostUIStyles.bottomCardContent, { backgroundColor: '#F57C00', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px', position: 'relative', top: -6, justifyContent: 'center' }]}
                >

                    <Typography sx={{ color: 'white' }}>{linkTo?.module === "Trip" ? 'Book trip' : linkTo?.module === "Itinerary" ? "Buy itenary" : null}</Typography>
                </Box>
            }
            {!isPreview && (
                <CardContent
                orientation="horizontal"
                sx={PostUIStyles.bottomCardContent}
                >
                    <Box sx={PostUIStyles.actionButtonsBox}>
                        <IconButton
                            variant="plain"
                            color="neutral"
                            size="sm"
                            onClick={handleLikeInternal}
                            >
                            {localIsLiked ? (
                                <FavoriteRoundedIcon sx={PostUIStyles.favoriteIcon} />
                            ) : (
                                <FavoriteBorderIcon />
                            )}
                            <Typography sx={PostUIStyles.likeCount}>
                                {localLikeCount}
                            </Typography>
                        </IconButton>
                        <IconButton onClick={handleCommentInternal} variant="plain" color="neutral" size="sm">
                            <img src={CommentIcon} alt="Comment" style={{ width: 22, height: 22 }} />
                            <Typography sx={PostUIStyles.likeCount}>
                                {localCommentCount}
                            </Typography>
                        </IconButton>
                        <IconButton onClick={handleShareInternal} variant="plain" color="neutral" size="sm">
                            <img src={ShareIcon} alt="Share" style={{ width: 26, height: 26 }} />
                        </IconButton>
                    </Box>
                    <IconButton onClick={handleBookmarkInternal} variant="plain" color="neutral" size="sm">
                        {localIsSaved ? (
                            <BookmarkIcon sx={{ color: COLORS.primary }} />
                        ) : (
                            <BookmarkBorderRoundedIcon />
                        )}
                    </IconButton>
                </CardContent>
            )}
            </Box>
        </>
    );
}